import React from "react";

/**
 * Sticky "Jump to" section nav.
 *
 * Built for the Risk dashboard's 2026-08-31 reorder and shared verbatim with the
 * Signals dashboard so the two pages cannot drift apart cosmetically. Both pages
 * anchor their sections with `scroll-mt-20` and ids of the form `<page>-sec-*`.
 *
 * z-30, not z-20: the Signals holdings grid carries a `sticky top-0 z-20` table header
 * that sits later in the DOM, and at equal z-index the later element wins — the grid's
 * header would slide over the nav while scrolling. Modals are z-[120] and stay above.
 *
 * Pills are rendered as buttons, not anchors: an `<a href="#id">` writes history
 * entries, so Back would walk the operator up the page one section at a time
 * instead of leaving the dashboard.
 */
export function QuickNav({
  items,
  className = "",
}: {
  items: Array<{ id: string; label: string }>;
  /** Outer spacing only. Keep it on THIS element: `position: sticky` sticks only while
   *  its containing block is on screen, so wrapping the nav in a spacer div (whose box is
   *  exactly the nav's height) silently turns the sticky behaviour off. */
  className?: string;
}) {
  return (
    <div className={`sticky top-2 z-30 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)]/90 backdrop-blur px-3 py-2 shadow-sm ${className}`.trim()}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] opacity-55 mr-1">
          Jump to
        </div>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            // Exposed so the screenshot gate can assert every pill's target actually
            // resolves, rather than just counting pills. Counting cannot catch a
            // renamed section id, which is the exact defect this nav already shipped
            // once (the "Detail" pill pointed at an id no element carried).
            data-target={item.id}
            onClick={() => {
              const el = document.getElementById(item.id);
              if (!el) return;
              el.scrollIntoView({ behavior: "smooth", block: "start" });
              // Move keyboard focus with the viewport, or the next Tab resumes from the
              // top of the document. preventScroll is required so focus does not fight
              // the smooth scroll above.
              el.setAttribute("tabindex", "-1");
              el.focus({ preventScroll: true });
            }}
            className="rounded-full border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[11px] font-medium opacity-80 transition hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live focus-visible:opacity-100"
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default QuickNav;
